// How what the engine found becomes what a person reads: the categories, the problems (one per rule,
// whatever the number of variants), what their numbers count, the common order and the summary. Pure
// functions: the dashboard and the WordPress plugin's API both read from here, so a figure is the same
// wherever it is shown. Tested in problems.test.ts and, through the dashboard, in results-logic.test.ts.
import type { WorkStatus } from './follow-up';
import { PRIORITY_RANK, type Priority } from './priority';

export type Category = 'problems' | 'unchecked' | 'bestPractices' | 'seo' | 'quality';
export type DeviceId = 'escritorio' | 'movil';
type SeverityId = 'critica' | 'grave' | 'moderada' | 'leve';

const SEVERITY_RANK: Record<SeverityId, number> = { critica: 0, grave: 1, moderada: 2, leve: 3 };

/**
 * Where an issue belongs. Problems are confirmed failures of the WCAG rules; what the automated test
 * could not decide is "unchecked"; best practices, SEO and quality are kept apart from the accessibility
 * problems and never counted as them.
 */
export function categoryOf(issue: { module: string; kind: string; result: string }): Category {
  if (issue.module === 'seo') return 'seo';
  if (issue.module === 'calidad') return 'quality';
  if (issue.result === 'incomplete') return 'unchecked';
  return issue.kind === 'wcag' ? 'problems' : 'bestPractices';
}

/** The tab of the results where a problem is listed. */
export const tabOf = (problem: {
  module: string;
  kind: string;
  result: string;
}): 'problems' | 'unchecked' | 'other' => {
  const category = categoryOf(problem);
  return category === 'problems' ? 'problems' : category === 'unchecked' ? 'unchecked' : 'other';
};

/** An element that several pages repeat, looked after on one of them. */
export interface SharedComponentLike {
  selector: string;
  html: string;
  identical: boolean;
  zone: string | null;
  zoneLabel?: string | null;
  pageKeys: string[];
  ownerKey?: string;
  ownerKind?: 'home' | 'first';
}

/** What an issue's numbers say on one page. */
export interface IssueStatLike {
  elements: number;
  appearances: number;
  devices: DeviceId[];
  capped: boolean;
  discarded: number;
  delegated?: number;
  global?: number;
}

/** The part of an issue (of the whole scan, or as found on one page) that problems are made of. */
export interface IssueLike {
  ruleId: string;
  module: 'accesibilidad' | 'seo' | 'calidad';
  kind: string;
  result: 'violation' | 'incomplete';
  severity: SeverityId;
  priority: Priority;
  title: string;
  description: string | null;
  fix: string | null;
  criteria: string[];
  criteriaDetail: unknown;
  affects: unknown;
  roles: unknown;
  origin?: 'automatico' | 'manual';
  devices: DeviceId[];
  elements: number;
  appearances: number;
  capped: boolean;
  discarded: number;
  pageKeys: string[];
  pageStats?: Record<string, IssueStatLike>;
  languages?: { codes: string[]; undeclared: number };
  shared?: SharedComponentLike[];
  followUp: {
    tracked: number;
    confirmed: number;
    work: Record<string, number>;
    check?: { verified: number; persists: number; pending: number };
  };
  /** Only on an issue as found on one page: its figures there. */
  onThisPage?: IssueStatLike;
}

export interface ProblemFollow {
  tracked: number;
  work: Record<WorkStatus, number>;
  confirmed: number;
  /** What the checks found on the elements the team follows. */
  check: { verified: number; persists: number; pending: number };
}

/**
 * A problem as a person sees it: one rule, once. Its variants are the technical groupings of its
 * elements (same rule, different kind of element) and are only shown in its detail.
 * `elements` counts distinct elements (one that fails on desktop and on mobile counts once);
 * `appearances` counts every time the check found one. On a page, the numbers are the ones on that page.
 */
export interface Problem<T extends IssueLike = IssueLike> {
  key: string;
  ruleId: string;
  module: 'accesibilidad' | 'seo' | 'calidad';
  /** What the rule is: a WCAG criterion, a best practice, a regulation item or information. */
  kind: string;
  result: 'violation' | 'incomplete';
  title: string;
  description: string | null;
  fix: string | null;
  criteria: string[];
  criteriaDetail: T['criteriaDetail'];
  affects: T['affects'];
  roles: T['roles'];
  priority: Priority;
  severity: SeverityId;
  /** The languages of the pages the problem is on, and how many of those pages do not say which they are. */
  languages: { codes: string[]; undeclared: number };
  /** Elements the problem has in common on several pages: probably one shared component each. */
  shared: SharedComponentLike[];
  elements: number;
  appearances: number;
  /** The engine stopped reporting elements somewhere: the figures are "at least". */
  capped: boolean;
  /** Pages with the problem in the whole scan. */
  pages: number;
  /** Pages other than this one (only meaningful in the view of a page). */
  otherPages: number;
  devices: DeviceId[];
  issues: T[];
  /** Found by the engine, or by a person following a guide of the manual checks. */
  origin: 'automatico' | 'manual';
  /** What the team follows among the elements of the problem, by work status, and what it confirmed. */
  follow: ProblemFollow;
  /** Elements the team discarded with a justification: the figures above leave them out. */
  discarded: number;
  /** Some of its elements are shared components (the same element on several pages): a global problem. */
  global: boolean;
  /** On a page: elements of shared components that another page looks after (not counted as this page's own). */
  delegated: number;
  /** On a page: it looks after a shared component for the other pages too. */
  owns: boolean;
}

/**
 * On a page, the problem is there only through shared components that another page looks after: it is not listed
 * as this page's own problem (a note points to the page that does), nor counted among its problems.
 */
export const isDelegatedOnly = (problem: Pick<Problem, 'result' | 'elements' | 'delegated'>) =>
  problem.result === 'violation' && problem.elements === 0 && problem.delegated > 0;

/** Every element of the problem is discarded: it is not among the problems to fix, nor in the totals. */
export const isFullyDiscarded = (problem: Pick<Problem, 'result' | 'elements' | 'discarded'>) =>
  problem.result === 'violation' && problem.elements === 0 && problem.discarded > 0;

const NO_WORK: Record<WorkStatus, number> = {
  pendiente: 0,
  'en-curso': 0,
  aplicada: 0,
  aplazada: 0,
  archivada: 0,
};

function followOf(issues: IssueLike[]): ProblemFollow {
  const work = { ...NO_WORK };
  const check = { verified: 0, persists: 0, pending: 0 };
  let tracked = 0;
  let confirmed = 0;
  for (const issue of issues) {
    tracked += issue.followUp.tracked;
    confirmed += issue.followUp.confirmed;
    check.verified += issue.followUp.check?.verified ?? 0;
    check.persists += issue.followUp.check?.persists ?? 0;
    check.pending += issue.followUp.check?.pending ?? 0;
    for (const status of Object.keys(work) as WorkStatus[]) work[status] += issue.followUp.work[status] ?? 0;
  }
  return { tracked, work, confirmed, check };
}

/** The shared components of the variants of one problem, the same one counted once (pages added). */
function mergeShared(components: SharedComponentLike[]): SharedComponentLike[] {
  const merged = new Map<string, SharedComponentLike>();
  for (const component of components) {
    const key = `${component.selector}\u0000${component.html}`;
    const before = merged.get(key);
    merged.set(
      key,
      before
        ? {
            ...before,
            pageKeys: [...new Set([...before.pageKeys, ...component.pageKeys])],
            // The home page looks after it whenever one of the variants says so.
            ...(before.ownerKind !== 'home' && component.ownerKind === 'home'
              ? { ownerKey: component.ownerKey, ownerKind: component.ownerKind }
              : {}),
          }
        : component,
    );
  }
  return [...merged.values()].sort((a, b) => b.pageKeys.length - a.pageKeys.length);
}

/**
 * The common order: priority first, then the elements affected, then the pages, then the name. It is
 * the same on every list, so "start at the top" means the same everywhere.
 */
export function byPriority(
  a: Pick<Problem, 'priority' | 'elements' | 'pages' | 'title'>,
  b: Pick<Problem, 'priority' | 'elements' | 'pages' | 'title'>,
): number {
  return (
    PRIORITY_RANK[a.priority] - PRIORITY_RANK[b.priority] ||
    b.elements - a.elements ||
    b.pages - a.pages ||
    a.title.localeCompare(b.title)
  );
}

/** Groups the issues of the same rule (and result) into problems, ordered by priority. */
export function groupProblems<T extends IssueLike>(issues: T[]): Problem<T>[] {
  const groups = new Map<string, T[]>();
  for (const issue of issues) {
    const key = `${issue.ruleId}|${issue.result}`;
    groups.set(key, [...(groups.get(key) ?? []), issue]);
  }
  const problems = [...groups].map(([key, list]): Problem<T> => {
    const first = list[0]!;
    const here = list.filter((issue) => issue.onThisPage !== undefined);
    const pageKeys = new Set(list.flatMap((issue) => issue.pageKeys));
    const stats: IssueStatLike[] =
      here.length > 0 ? here.map((issue) => issue.onThisPage as IssueStatLike) : list;
    const devices = new Set<DeviceId>(
      here.length > 0
        ? here.flatMap((issue) => issue.onThisPage?.devices ?? [])
        : list.flatMap((issue) => issue.devices),
    );
    const worst = list.reduce(
      (found, issue) => (PRIORITY_RANK[issue.priority] < PRIORITY_RANK[found.priority] ? issue : found),
      first,
    );
    const mostSevere = list.reduce(
      (found, issue) => (SEVERITY_RANK[issue.severity] < SEVERITY_RANK[found] ? issue.severity : found),
      first.severity,
    );
    return {
      key,
      ruleId: first.ruleId,
      module: first.module,
      kind: first.kind,
      result: first.result,
      title: first.title,
      description: first.description,
      fix: first.fix,
      criteria: first.criteria,
      criteriaDetail: first.criteriaDetail,
      affects: first.affects,
      roles: first.roles,
      priority: worst.priority,
      severity: mostSevere,
      languages: {
        codes: [...new Set(list.flatMap((issue) => issue.languages?.codes ?? []))].sort(),
        undeclared: Math.max(0, ...list.map((issue) => issue.languages?.undeclared ?? 0)),
      },
      shared: mergeShared(list.flatMap((issue) => issue.shared ?? [])),
      elements: stats.reduce((sum, stat) => sum + stat.elements, 0),
      appearances: stats.reduce((sum, stat) => sum + stat.appearances, 0),
      capped: stats.some((stat) => stat.capped),
      pages: pageKeys.size,
      otherPages: Math.max(0, pageKeys.size - (here.length > 0 ? 1 : 0)),
      devices: (['escritorio', 'movil'] as DeviceId[]).filter((device) => devices.has(device)),
      issues: list,
      origin: first.origin ?? 'automatico',
      follow: followOf(list),
      discarded:
        here.length > 0
          ? here.reduce((sum, issue) => sum + (issue.onThisPage?.discarded ?? 0), 0)
          : list.reduce((sum, issue) => sum + issue.discarded, 0),
      global: list.some((issue) => (issue.shared?.length ?? 0) > 0),
      delegated: here.reduce((sum, issue) => sum + (issue.onThisPage?.delegated ?? 0), 0),
      owns: here.some((issue) => (issue.onThisPage?.global ?? 0) > 0),
    };
  });
  return problems.sort(byPriority);
}

/** The issues of a scan (or of a page) split into the categories that are never mixed. */
export function splitCategories<T extends { module: string; kind: string; result: string }>(
  issues: T[],
): Record<Category, T[]> {
  const split: Record<Category, T[]> = {
    problems: [],
    unchecked: [],
    bestPractices: [],
    seo: [],
    quality: [],
  };
  for (const issue of issues) split[categoryOf(issue)].push(issue);
  return split;
}

/** What a page of the scan tells the summary. */
export interface SummaryPage {
  key: string;
  blocked: boolean;
  error: string | null;
  /** Null for pages that could not be analysed, and for scans saved before the counts existed. */
  counts: { nodes: number } | null;
}

export interface ResultsSummary {
  /** Distinct problems (one per rule). */
  problems: number;
  /** Of them, the ones with shared components (the same element on several pages): global problems. */
  global: number;
  byPriority: Record<'alta' | 'media' | 'baja', number>;
  /** Distinct elements in the scan: an element that fails in several problems, or on both devices, counts once. */
  elements: number;
  /** What the problems add up to; larger than `elements` when the same element fails in several of them. */
  elementsSummed: number;
  /** The same element fails in more than one problem: the sum of the cards is not the total. */
  repeated: boolean;
  capped: boolean;
  pagesAffected: number;
  pagesAnalysed: number;
}

/**
 * The summary of the problems of a scan (those of the `problems` category: confirmed failures of the
 * WCAG rules). The elements come from the pages' own counts (each page counts
 * its distinct elements once, across problems and devices); only a scan saved before they existed falls
 * back to the sum of the problems.
 */
export function summarize(problems: Problem[], pages: SummaryPage[]): ResultsSummary {
  const confirmed = problems.filter(
    (problem) => problem.result === 'violation' && !isFullyDiscarded(problem),
  );
  const analysed = pages.filter((page) => !page.blocked && !page.error);
  const summed = confirmed.reduce((sum, problem) => sum + problem.elements, 0);
  const counted = analysed.length > 0 && analysed.every((page) => page.counts);
  const elements = counted ? analysed.reduce((sum, page) => sum + (page.counts?.nodes ?? 0), 0) : summed;
  const byPriorityCount = { alta: 0, media: 0, baja: 0 };
  for (const problem of confirmed) if (problem.priority !== 'pendiente') byPriorityCount[problem.priority]++;
  return {
    problems: confirmed.length,
    global: confirmed.filter((problem) => problem.global).length,
    byPriority: byPriorityCount,
    elements,
    elementsSummed: summed,
    repeated: summed > elements,
    capped: confirmed.some((problem) => problem.capped),
    pagesAffected: new Set(confirmed.flatMap((problem) => problem.issues.flatMap((issue) => issue.pageKeys)))
      .size,
    pagesAnalysed: analysed.length,
  };
}

/** What one page has, in the same units as the summary: problems (one per rule) and the elements behind them. */
export interface PageProblems {
  problems: number;
  byPriority: Record<'alta' | 'media' | 'baja', number>;
  /** The page's own distinct elements (escritorio and móvil together), as the engine counted them. */
  elements: number;
  /** Of its problems, the ones that are shared components this page looks after for the others too. */
  global: number;
  /** Problems it only has through shared components another page looks after (not among its own). */
  delegated: number;
  /** The highest priority among those. */
  delegatedTop: 'alta' | 'media' | 'baja' | null;
}

/** Highest-priority problem on one page that counts in the list (not discarded, not delegated-only). */
export function topProblemKeyForPage(problems: Problem[], pageKey: string): string | null {
  const onPage = problems.filter((problem) => {
    if (problem.result !== 'violation' || isFullyDiscarded(problem)) return false;
    if (!problem.issues.some((issue) => issue.pageKeys.includes(pageKey))) return false;
    const here = problem.issues
      .filter((issue) => issue.pageKeys.includes(pageKey))
      .map((issue) => issue.pageStats?.[pageKey]);
    const onlyDelegated =
      here.length > 0 &&
      here.every(
        (stat) =>
          stat !== undefined && (stat.delegated ?? 0) > 0 && stat.elements - (stat.delegated ?? 0) <= 0,
      );
    return !onlyDelegated;
  });
  if (onPage.length === 0) return null;
  return [...onPage].sort(byPriority)[0]!.key;
}

/**
 * The problems of each page of the scan, counted as the summary counts them: a problem is a rule, once, and a fully
 * discarded one is not among them. A problem a page has only through shared components that another page looks
 * after (a header, a footer, a menu) is that page's too little to count again: it counts where it is looked after
 * (the home page when it is one of the pages), so the same cause is not added up page by page. A rule can still count
 * on several pages when each has elements of its own.
 */
export function problemsByPage(problems: Problem[], pages: SummaryPage[]): Map<string, PageProblems> {
  const byPage = new Map<string, PageProblems>(
    pages.map((page) => [
      page.key,
      {
        problems: 0,
        byPriority: { alta: 0, media: 0, baja: 0 },
        elements: page.counts?.nodes ?? 0,
        global: 0,
        delegated: 0,
        delegatedTop: null,
      },
    ]),
  );
  for (const problem of problems) {
    if (problem.result !== 'violation' || isFullyDiscarded(problem)) continue;
    const keys = new Set(problem.issues.flatMap((issue) => issue.pageKeys));
    for (const key of keys) {
      const entry = byPage.get(key);
      if (!entry) continue;
      const here = problem.issues
        .filter((issue) => issue.pageKeys.includes(key))
        .map((issue) => issue.pageStats?.[key]);
      // Only what other pages look after: not this page's problem.
      const onlyDelegated = here.every(
        (stat) =>
          stat !== undefined && (stat.delegated ?? 0) > 0 && stat.elements - (stat.delegated ?? 0) <= 0,
      );
      if (onlyDelegated) {
        entry.delegated++;
        if (
          problem.priority !== 'pendiente' &&
          (entry.delegatedTop === null || PRIORITY_RANK[problem.priority] < PRIORITY_RANK[entry.delegatedTop])
        )
          entry.delegatedTop = problem.priority;
        continue;
      }
      entry.problems++;
      if (here.some((stat) => (stat?.global ?? 0) > 0)) entry.global++;
      if (problem.priority !== 'pendiente') entry.byPriority[problem.priority]++;
    }
  }
  return byPage;
}
